/**
 * キーボードガイドの配置計算(docs/02_game_design.md §3)。
 *
 * `ui/components/KeyboardGuide.tsx` と `ui/components/HandsOverlay.tsx` が
 * **同じ座標系**を共有するために、レイアウトだけをここへ切り出している。
 * 手のオーバーレイは「そのキーの中心」へ指先を伸ばすので、キー位置が要る。
 *
 * 描画非依存の純関数なので単体テストできる(CLAUDE.md ルール1と同じ流儀)。
 */

import { KEYBOARD_ROWS, type FingerId, type KeyDef } from './keyboard';

/** 標準キー1つぶんの幅(px。SVG のユーザー座標)。 */
export const KEY_UNIT = 64;
/** 横のキー間隔。 */
export const KEY_GAP = 6;
/** キーの高さ。 */
export const KEY_HEIGHT = 62;
/** 段の間隔。 */
export const ROW_GAP = 6;

export interface PlacedKey {
  readonly key: KeyDef;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface KeyboardLayout {
  readonly keys: readonly PlacedKey[];
  readonly width: number;
  readonly height: number;
}

function build(): KeyboardLayout {
  const rows = KEYBOARD_ROWS.map((row) => {
    let x = 0;
    const placed = row.map((key) => {
      const width = (key.width ?? 1) * KEY_UNIT;
      const item = { key, x, width };
      x += width + KEY_GAP;
      return item;
    });
    return { placed, width: Math.max(0, x - KEY_GAP) };
  });

  const width = Math.max(...rows.map((row) => row.width));
  const keys: PlacedKey[] = [];
  rows.forEach((row, rowIndex) => {
    // スペース段だけ中央寄せ。
    const offset = rowIndex === KEYBOARD_ROWS.length - 1 ? (width - row.width) / 2 : 0;
    for (const item of row.placed) {
      keys.push({
        key: item.key,
        x: item.x + offset,
        y: rowIndex * (KEY_HEIGHT + ROW_GAP),
        width: item.width,
        height: KEY_HEIGHT,
      });
    }
  });
  return { keys, width, height: KEYBOARD_ROWS.length * (KEY_HEIGHT + ROW_GAP) - ROW_GAP };
}

export const KEYBOARD_LAYOUT: KeyboardLayout = build();

/** SVG の余白(枠線がにじまないぶん)。キーボードと手で必ず同じ値を使う。 */
export const LAYOUT_PADDING = 4;

/**
 * キーボードと手のオーバーレイで共有する viewBox。
 * 同じ箱・同じ viewBox なので、`preserveAspectRatio` の既定値でぴったり重なる。
 */
export const LAYOUT_VIEW_BOX = `${-LAYOUT_PADDING} ${-LAYOUT_PADDING} ${
  KEYBOARD_LAYOUT.width + LAYOUT_PADDING * 2
} ${KEYBOARD_LAYOUT.height + LAYOUT_PADDING * 2}`;

const BY_ID = new Map(KEYBOARD_LAYOUT.keys.map((placed) => [placed.key.id, placed]));

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** キーIDの中心座標(未知のIDは null)。 */
export function keyCenter(keyId: string): Point | null {
  const placed = BY_ID.get(keyId);
  if (!placed) return null;
  return { x: placed.x + placed.width / 2, y: placed.y + placed.height / 2 };
}

/** 指のホームポジション(ここに指先を置く)。親指はスペースの左右に分かれる。 */
export const HOME_KEY_OF: Readonly<Record<Exclude<FingerId, 'thumb'>, string>> = {
  'l-pinky': 'KeyA',
  'l-ring': 'KeyS',
  'l-middle': 'KeyD',
  'l-index': 'KeyF',
  'r-index': 'KeyJ',
  'r-middle': 'KeyK',
  'r-ring': 'KeyL',
  'r-pinky': 'Semicolon',
};

/** 指のホームポジション座標。 */
export function homeCenter(finger: Exclude<FingerId, 'thumb'>): Point {
  // HOME_KEY_OF は実在キーだけを指すので必ず引ける。
  return keyCenter(HOME_KEY_OF[finger]) ?? { x: 0, y: 0 };
}
